import {
  ArrowLeft,
  MapPin,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";

const SITE = "https://cphhighlights.com";

// Own photos (uploaded by the site owner)
const IMG_KONGENS_HAVE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/8krr17pt_2026-09-04%2014_09_23.422%2B0200.webp";
const IMG_RADHUSPLADSEN =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/188s2dd6_2026-09-04%2014_08_14.756%2B0200.jpg";
const IMG_NYHAVN67 =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/qnk92ep7_2026-09-04%2014_07_30.384%2B0200.webp";
const IMG_GRAVE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/biaht20l_2025-12-28%2019_00_42.459%2B0100.webp";
const IMG_MANHOLE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/tvlqkuej_2025-12-28%2016_47_18.865%2B0100.webp";
const IMG_APOTEK = "/images/hca/apotek.webp";
const mapUrl = (q) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

const MEET = [
  {
    slug: "kongens-have",
    name: "The statue in Kongens Have",
    tag: "The royal one",
    img: IMG_KONGENS_HAVE,
    alt: "Bronze statue of H.C. Andersen seated with a book, in Kongens Have",
    body: "He's sat here in bronze since 1880 — book open, hand raised, mid-story, as if hushing the King's Garden around him. Find him by Rosenborg Castle, in the park he loved to walk.",
    map: "H.C. Andersen Statue, Kongens Have, København",
  },
  {
    slug: "radhuspladsen",
    name: "The statue by City Hall",
    tag: "The grand one",
    img: IMG_RADHUSPLADSEN,
    alt: "Full bronze statue of H.C. Andersen with top hat, cane and book by Rådhuspladsen",
    body: "On H.C. Andersens Boulevard, facing the Tivoli gates he adored — top hat on, cane in hand, and a lap rubbed golden by decades of visitors climbing up to sit with him for a photo.",
    map: "H.C. Andersen statue, Rådhuspladsen, København",
  },
  {
    slug: "nyhavn-67",
    name: "His home at Nyhavn 67",
    tag: "The long home",
    img: IMG_NYHAVN67,
    alt: "The arched wooden door of Nyhavn 67 with H.C. Andersen's memorial plaque",
    body: "His longest stay (1848–65): three rooms on the second floor, lodging with the Anholm family on the sunny side of the canal. The memorial plaque by the arched wooden door marks it — and the harbour view hasn't changed much since.",
    map: "Nyhavn 67, København",
  },
  {
    slug: "magasin",
    name: "The attic room at Magasin",
    tag: "The hidden room",
    img: null,
    alt: null,
    body: "Before Nyhavn came a year (1827–28) in a tiny attic room inside what is now the Magasin du Nord department store on Kongens Nytorv. The room is preserved, almost untouched for 200 years — ask the sales staff and they'll help you find your way up.",
    map: "Magasin du Nord, Kongens Nytorv, København",
    linkLabel: "Find it here",
  },
  {
    slug: "apotek",
    name: "His table at Det Lille Apotek",
    tag: "Where he ate",
    img: IMG_APOTEK,
    alt: "Det Lille Apotek restaurant on the corner of Store Kannikestræde and Lille Kannikestræde",
    body: "The city's oldest restaurant (open since 1720) was one of his regular tables — he lived in rented rooms nearby and came here for hearty, affordable dinners. Still candlelit, still crooked, still serving the classics he ate.",
    map: "Det Lille Apotek, Store Kannikestræde 15, København",
  },
  {
    slug: "flying-trunk",
    name: "Den flyvende kuffert at Tivoli",
    tag: "His tales, as a ride",
    img: null,
    alt: null,
    body: "Inside Tivoli — the gardens Andersen adored and visited again and again — a slow little boat carries you through dozens of hand-built scenes from his fairy tales, from the Tinderbox to the Nightingale. Named after his story 'The Flying Trunk', it's unhurried, low-key magic that charms grown-ups as much as children.",
    map: "Den flyvende kuffert, Tivoli, København",
    linkLabel: "Find it in Tivoli",
  },
  {
    slug: "assistens",
    name: "His grave at Assistens Kirkegård",
    tag: "The Poet",
    img: IMG_GRAVE,
    alt: "H.C. Andersen's gravestone at Assistens Cemetery with fresh flowers",
    body: "\u201CDigteren — The Poet.\u201D A simple stone under the trees in Nørrebro's beautiful park-cemetery, still dressed with fresh flowers. Locals picnic on the grass all around him.",
    map: "H.C. Andersens grav, Assistens Kirkegård, København",
  },
  {
    slug: "little-mermaid",
    name: "The Little Mermaid",
    tag: "His most famous character",
    img: null,
    alt: null,
    body: "Not him — her. The heroine of his 1837 tale has sat on her rock at Langelinie since 1913, gazing back at the shore she gave everything up for. Small, shy and utterly unmissable. (No photo — images of the statue belong to the sculptor's family, so we keep our camera politely in our pocket.)",
    map: "The Little Mermaid, Langelinie, København",
    linkLabel: "Find her here",
  },
  {
    slug: "manhole",
    name: "The manhole covers",
    tag: "The hidden ones",
    img: IMG_MANHOLE,
    alt: "Cast-iron manhole cover with H.C. Andersen's profile silhouette",
    body: "Look down. The old cast-iron covers from Københavns Energi carry his top-hatted profile — scattered at random spots all over the city. Spotting one is a tiny local treasure hunt.",
    map: null,
  },
];

const LIVED = [
  {
    years: "1834–38",
    address: "Nyhavn 20",
    body: "Moved in on 1 September 1834 after his first big trip abroad. His first fairy-tale collection came out here in 1835 — The Tinderbox, The Princess and the Pea and Little Claus and Big Claus.",
  },
  {
    years: "1848–65",
    address: "Nyhavn 67",
    body: "His longest stay in Nyhavn: three rooms on the second floor with the Anholm family. Along with No. 18, this is the house people mean when they say 'the H.C. Andersen house'.",
  },
  {
    years: "1871–75",
    address: "Nyhavn 18",
    body: "His last Nyhavn address — a first-floor flat from September 1871 until his final years. The other house everyone photographs.",
  },
];

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "H.C. Andersen in Copenhagen", item: `${SITE}/hc-andersen` },
  ],
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Hans Christian Andersen in Copenhagen — Statues, Nyhavn Homes & His Grave",
  url: `${SITE}/hc-andersen`,
  description:
    "Meet H. C. Andersen all over Copenhagen — his statue in Kongens Have and by City Hall, the Nyhavn houses where he lived, his grave at Assistens Cemetery, The Little Mermaid and the manhole covers carrying his profile.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const PERSON = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Hans Christian Andersen",
  alternateName: "H.C. Andersen",
  jobTitle: "Author",
  description:
    "Danish author of fairy tales including The Little Mermaid, The Ugly Duckling and The Emperor's New Clothes. Lived most of his life in Copenhagen.",
  birthDate: "1805-04-02",
  deathDate: "1875-08-04",
  birthPlace: { "@type": "Place", name: "Odense, Denmark" },
  url: `${SITE}/hc-andersen`,
};

export default function HcAndersenPage() {
  return (
    <main data-testid="hcandersen-page" className="pt-16 md:pt-20">
      <Seo
        title="Hans Christian Andersen in Copenhagen — Statues, Nyhavn Homes & His Grave"
        description="Meet H.C. Andersen in Copenhagen — his statues, the Nyhavn houses where he lived, his grave at Assistens Cemetery and The Little Mermaid."
        path="/hc-andersen"
        image={IMG_KONGENS_HAVE}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(PERSON)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMG_KONGENS_HAVE}
            alt="H.C. Andersen statue in Kongens Have, Copenhagen"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="hcandersen-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="hcandersen-share" />
          </div>
          <p className="font-body font-800 uppercase tracking-[0.2em] text-white text-xs">
            The fairy-tale writer
          </p>
          <h1 className="font-display font-900 tracking-tighter leading-[0.92] text-5xl md:text-7xl lg:text-8xl mt-4">
            Hans Christian <span className="font-editorial italic font-600">Andersen's Copenhagen</span>
          </h1>
          <p className="font-body text-white/75 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
            He arrived in Copenhagen at fourteen with nothing but a belief in his own stories — and the
            city never let him go. Statues, doorways and even manhole covers still carry his silhouette.
            Here's where to meet him.
          </p>
        </div>
      </section>

      {/* his story in short */}
      <section className="bg-[#F9F8F6] border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <p className="font-body font-800 uppercase tracking-[0.2em] text-[#DF3226] text-xs">
              His story, in short
            </p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.92] text-4xl md:text-5xl mt-3">
              From Odense to <span className="font-editorial italic font-600">the world</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="font-body text-base md:text-lg text-black/75 leading-relaxed space-y-4">
              <p>
                Born to a shoemaker in Odense, Denmark in 1805, he came to Copenhagen at 14 to chase the theatre.
                The stage rejected him — so he wrote instead. In 1835 the first fairy tales appeared, and
                the world has never stopped reading them: <em>The Little Mermaid</em>,{" "}
                <em>The Ugly Duckling</em>, <em>The Emperor's New Clothes</em>.
              </p>
              <p>
                He never really left the city again. He lived in three different houses on Nyhavn, walked
                Kongens Have almost daily, adored Tivoli — and when he died in 1875, the whole country
                mourned him as <strong>Digteren</strong>: The Poet.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* meet him in the city */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <p className="font-body font-800 uppercase tracking-[0.2em] text-[#DF3226] text-xs">Around the city</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.92] text-4xl md:text-5xl mt-3 max-w-3xl">
              Meet him in <span className="font-editorial italic font-600">the city</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {MEET.map((s, i) => (
              <Reveal key={s.slug} delay={Math.min(i, 3) * 0.06}>
                <div
                  data-testid={`hcandersen-meet-${s.slug}`}
                  className="bg-[#F9F8F6] border border-zinc-200 shadow-brut overflow-hidden h-full flex flex-col"
                >
                  {s.img && (
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={s.img} alt={s.alt} className="w-full h-full object-cover" loading="lazy" />
                      <span className="absolute top-3 left-3 font-body font-800 uppercase tracking-widest text-[10px] bg-[#DF3226] text-white px-2.5 py-1">
                        {s.tag}
                      </span>
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    {!s.img && (
                      <span className="font-body font-800 uppercase tracking-widest text-[10px] bg-[#DF3226] text-white px-2.5 py-1 w-fit mb-3">
                        {s.tag}
                      </span>
                    )}
                    <h3 className="font-display font-900 tracking-tight text-xl leading-tight">{s.name}</h3>
                    <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed flex-1">{s.body}</p>
                    {s.map && (
                      <a
                        href={mapUrl(s.map)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`hcandersen-map-${s.slug}`}
                        className="inline-flex items-center gap-1.5 font-body font-700 text-sm text-[#DF3226] mt-4 hover:gap-2.5 transition-all w-fit"
                      >
                        <MapPin size={14} /> {s.linkLabel || "Find him here"}
                      </a>
                    )}
                    {!s.map && (
                      <p className="font-body font-700 text-sm text-black/50 mt-4 italic">
                        Random locations — keep an eye on the pavement.
                      </p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* where he lived */}
      <section className="bg-[#13141A] text-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <p className="font-body font-800 uppercase tracking-[0.2em] text-[#DF3226] text-xs">
              Where he lived
            </p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.92] text-4xl md:text-5xl mt-3 max-w-3xl">
              The Nyhavn <span className="font-editorial italic font-600">years</span>
            </h2>
            <p className="font-body text-white/75 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
              Three addresses on the postcard canal, two with memorial plaques, all private homes
              today, so admire them from the quay.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {LIVED.map((h, i) => (
              <Reveal key={h.address} delay={i * 0.06}>
                <a
                  href={mapUrl(`${h.address}, København`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hcandersen-lived-${h.address.replace(/\s+/g, "-").toLowerCase()}`}
                  className="block bg-white/10 border border-white/15 p-5 hover:bg-white/20 transition-colors group h-full"
                >
                  <span className="font-display font-900 text-[#DF3226] text-2xl">{h.years}</span>
                  <span className="font-display font-800 text-xl mt-2 flex items-center gap-2">
                    {h.address}
                    <MapPin size={15} className="text-[#DF3226] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                  <span className="block font-body text-sm text-white/75 mt-2 leading-relaxed">{h.body}</span>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="font-body text-sm text-white/60 mt-6 max-w-2xl leading-relaxed italic">
              One correction to the postcards: he didn't die in Nyhavn. His last days were spent at
              Rolighed, the Melchior family's country house on Østerbro, where he died on 4 August 1875.
            </p>
          </Reveal>
        </div>
      </section>

      {/* quote band */}
      <section className="bg-[#DF3226] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center">
          <Reveal>
            <span aria-hidden="true" className="block font-display font-900 text-7xl md:text-8xl leading-none text-white/25">&ldquo;</span>
            <p
              data-testid="hcandersen-quote"
              className="font-editorial italic font-600 text-3xl md:text-5xl leading-[1.1] max-w-3xl mx-auto -mt-2"
            >
              To travel is to live.
            </p>
            <p className="font-body font-700 text-xs uppercase tracking-[0.3em] text-white/70 mt-6">
              Hans Christian Andersen
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <h2 className="font-display font-900 tracking-tighter leading-[0.92] text-3xl md:text-5xl">
              Walk his city <span className="font-editorial italic font-600">yourself</span>
            </h2>
            <p className="font-body text-white/70 text-base md:text-lg mt-4 max-w-xl leading-relaxed">
              The itinerary passes his Nyhavn houses and the Kongens Have statue — the planner can add
              the rest.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/copenhagen-itinerary"
              data-testid="hcandersen-cta-itinerary"
              className="inline-flex items-center gap-2 font-display font-900 text-base bg-[#DF3226] text-white px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-xl transition-all"
            >
              The 3-day itinerary
            </Link>
            <Link
              to="/planner"
              data-testid="hcandersen-cta-planner"
              className="inline-flex items-center gap-2 font-display font-900 text-base bg-white text-black px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-xl transition-all"
            >
              Plan my trip
            </Link>
          </div>
        </div>
      </section>

      <GygTourBand tourId="851143" />
      <ExploreLinks
        testid="explore-hca"
        links={[
          { label: "The prettiest corners of his city — the photo-worthy streets", to: "/photo-worthy-streets" },
          { label: "Day 1 passes his house — the 3-day itinerary", to: "/copenhagen-itinerary" },
          { label: "Fairy tales hit different with kids — the family guide", to: "/copenhagen-with-kids" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
